import React, { useEffect } from "react";
import { useNavigate, useLocation } from "react-router-dom";
import {
  Box,
  Drawer,
  List,
  ListItemButton,
  ListItemIcon,
  ListItemText,
  Toolbar,
  Typography,
  useMediaQuery,
  Divider,
} from "@mui/material";
import {
  FaChartBar,
  FaUserGraduate,
  FaChalkboardTeacher,
  FaUserTie,
  FaFileInvoice,
  FaWallet,
  FaBullhorn,
  FaSignOutAlt,
  FaBook,
  FaTasks,
  FaBoxOpen,
  FaLayerGroup,
  FaListAlt,
} from "react-icons/fa";

const DRAWER_WIDTH = 240;
const MINI_WIDTH = 0;

export default function Sidebar({ open = true, setOpen = () => {} }) {
  const navigate = useNavigate();
  const location = useLocation();
  const isMobile = useMediaQuery("(max-width:900px)");

  useEffect(() => {
    try {
      localStorage.setItem("sidebar_open", open ? "1" : "0");
    } catch (e) {}
  }, [open]);

  const sections = [
    {
      title: "Dashboard",
      items: [{ key: "dashboard", label: "Dashboard", icon: <FaChartBar />, to: "/admin/dashboard" }],
    },
    {
      title: "Students",
      items: [
        { key: "addStudent", label: "Add Student", icon: <FaUserGraduate />, to: "/admin/add-student" },
        { key: "viewStudent", label: "View Student", icon: <FaListAlt />, to: "/admin/view-student" },
        { key: "studentBilling", label: "Student Billing", icon: <FaFileInvoice />, to: "/admin/student-billing" },
      ],
    },
    {
      title: "Products",
      items: [
        { key: "productCategory", label: "Add Category", icon: <FaLayerGroup />, to: "/admin/product-category" },
        { key: "productSub", label: "Add Sub Category", icon: <FaBoxOpen />, to: "/admin/product-sub-category" },
        { key: "productAdd", label: "Add Product", icon: <FaBoxOpen />, to: "/admin/product-bundle/create" },
        { key: "productList", label: "List Product", icon: <FaListAlt />, to: "/admin/product-bundle/list" },
      ],
    },
    {
      title: "Subjects",
      items: [
        { key: "subjectAdd", label: "Add Subject", icon: <FaBook />, to: "/admin/subject-add" },
        { key: "assignsubject", label: "Assign Subject", icon: <FaTasks />, to: "/admin/subject-class/assign" },
      ],
    },
    {
      title: "Teachers",
      items: [
        { key: "addteacher", label: "Add Teacher", icon: <FaChalkboardTeacher />, to: "/admin/teacher-add" },
        { key: "assignsubjecttoteacher", label: "Assign Subject to Teacher", icon: <FaUserTie />, to: "/admin/teacher-subject/assign" },
      ],
    },
    {
      title: "Accounts",
      items: [
        { key: "wallet", label: "Wallet History", icon: <FaWallet />, to: "/admin/wallet-history" },
      ],
    },
    {
      title: "Communication",
      items: [{ key: "notice", label: "Notice", icon: <FaBullhorn />, to: "/admin/notice" }],
    },
  ];

  const handleNavigate = (to) => {
    if (isMobile) setOpen(false);
    if (to) navigate(to);
  };

  return (
    <Box component="nav" sx={{ width: open ? DRAWER_WIDTH : MINI_WIDTH, flexShrink: 0 }}>
      <Drawer
        variant="persistent"
        open={open}
        sx={{
          "& .MuiDrawer-paper": {
            width: open ? DRAWER_WIDTH : MINI_WIDTH,
            transition: "width 0.3s ease",
            overflowX: "hidden",
            bgcolor: "#0f3b66",
            color: "white",
            borderRight: 0,
          },
        }}
      >
        {/* Sidebar Header */}
        <Toolbar sx={{ display: "flex", alignItems: "center", justifyContent: "center", px: 2 }}>
          {open ? (
            <Typography sx={{ fontWeight: 800, letterSpacing: 0.5, fontSize: 18 }}>
              🎓 Admin Panel
            </Typography>
          ) : (
            "🎓"
          )}
        </Toolbar>

        <Divider sx={{ borderColor: "rgba(255,255,255,0.1)" }} />

        {/* Sections */}
        <List sx={{ p: 1 }}>
          {sections.map((section, idx) => (
            <Box key={section.title} sx={{ mb: 2 }}>
              {open && (
                <Typography
                  sx={{
                    px: 1.25,
                    py: 0.5,
                    fontSize: 12,
                    fontWeight: 700,
                    textTransform: "uppercase",
                    color: "rgba(255,255,255,0.6)",
                  }}
                >
                  {section.title}
                </Typography>
              )}

              {section.items.map((m) => {
                const active = location.pathname === m.to;
                return (
                  <ListItemButton
                    key={m.key}
                    onClick={() => handleNavigate(m.to)}
                    sx={{
                      mb: 0.5,
                      borderRadius: 1.5,
                      py: 1.1,
                      px: 1.25,
                      bgcolor: active ? "rgba(255,255,255,0.12)" : "transparent",
                      transition: "all 0.2s ease",
                      "&:hover": {
                        transform: "translateX(4px)",
                        bgcolor: "rgba(255,255,255,0.08)",
                      },
                    }}
                  >
                    <ListItemIcon sx={{ minWidth: 44 }}>
                      <Box
                        sx={{
                          width: 36,
                          height: 36,
                          borderRadius: 1,
                          display: "grid",
                          placeItems: "center",
                          bgcolor: active ? "rgba(255,255,255,0.2)" : "rgba(255,255,255,0.08)",
                          color: "white",
                          fontSize: 18,
                        }}
                      >
                        {m.icon}
                      </Box>
                    </ListItemIcon>

                    {open && (
                      <ListItemText
                        primary={m.label}
                        primaryTypographyProps={{
                          sx: {
                            fontWeight: active ? 800 : 600,
                            fontSize: 14,
                            color: "rgba(255,255,255,0.95)",
                          },
                        }}
                      />
                    )}
                  </ListItemButton>
                );
              })}

              {idx < sections.length - 1 && open && (
                <Divider sx={{ borderColor: "rgba(255,255,255,0.05)", mt: 1 }} />
              )}
            </Box>
          ))}

          {/* Logout */}
          <Box sx={{ mt: 2 }}>
            <ListItemButton
              onClick={() => {
                localStorage.clear();
                navigate("/login");
              }}
              sx={{
                borderRadius: 1.5,
                py: 1,
                px: 1.25,
                "&:hover": { bgcolor: "rgba(255,255,255,0.08)", transform: "translateX(4px)" },
              }}
            >
              <ListItemIcon sx={{ minWidth: 44 }}>
                <Box
                  sx={{
                    width: 36,
                    height: 36,
                    borderRadius: 1,
                    display: "grid",
                    placeItems: "center",
                    bgcolor: "rgba(255,255,255,0.08)",
                    color: "white",
                  }}
                >
                  <FaSignOutAlt />
                </Box>
              </ListItemIcon>
              {open && (
                <ListItemText
                  primary="Logout"
                  primaryTypographyProps={{ sx: { fontWeight: 700, fontSize: 14, color: "rgba(255,255,255,0.95)" } }}
                />
              )}
            </ListItemButton>
          </Box>
        </List>
      </Drawer>
    </Box>
  );
}
